import {
  colorBackgroundAltInverse,
  colorStatusAlertDefault,
} from '@duxton-web/design-tokens/themes/light';
import {
  ArrowLeftUniversal,
  CopyOutlined,
  EditOutlined,
  MoreVerticalUniversal,
  StopUniversal,
  UndoUniversal,
} from '@duxton-web/icons';
import {
  Button,
  Divider,
  Dropdown,
  Flex,
  Modal,
  Spin,
  Tabs,
  Typography,
} from '@duxton-web/ui';
import scribe from '@grab-x/scribe-web-sdk';
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import _ from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// If design changes to phase 1, swap out PerformancePage for SmartCampaignPerformance
import PerformancePage from 'omega-rtc/views/ucd/PerformancePage';
// import SmartCampaignPerformance from 'omega-rtc/views/ucd/SmartCampaignPerformance.jsx';
import { getFeatureFlagConfig } from 'actions/newTridentExtra';
import {
  GET_UCD_CAMPAIGN,
  GET_UCD_PROJECT,
  getUCDCampaign,
  getUCDProject,
  resetUCDCampaign,
  UPDATE_UCD_CAMPAIGN_STATUS,
  updateUCDCampaignStatus,
} from 'omega-rtc/actions/ucd';
import CampaignDuplicateConfirmationPopup from 'omega-rtc/components/form/ucd/CampaignDuplicateConfirmationPopup';
import ScribeEventName from 'omega-rtc/constants/scribe';
import {
  SMART_CAMPAIGN_STATUS_UPDATE_TYPES as ACTION_TYPE,
  TRIDENT_CAMPAIGN_TEMPLATE,
  UCD_ROUTES_LINK,
} from 'omega-rtc/constants/ucd';
import { SmartCampaignStatus, UcdCampaignResourceName, UcdUniversalScope } from 'omega-rtc/data/trident-campaigns';
import { isRequestsPending } from 'omega-rtc/utils/request-state-utils';
import { processCampaignData } from 'omega-rtc/utils/scribe';
import { isTaiwanCampaignCreationAllowed } from 'omega-rtc/utils/taiwan-campaign-operation';
import { getRequestStateKey } from 'omega-rtc/utils/ucd-utils';
import { getValueByFeatureFlag, hasPermission, hasTridentPermission } from 'omega-rtc/utils/user-utils';
import SmartCampaignOverview from 'omega-rtc/views/ucd/SmartCampaignOverview';
import VersionHistoryList from 'omega-rtc/views/trident-campaigns/common/VersionHistoryList';

dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);

// Show snackBar instead of toastr, on error from API Middleware
const showSnackBarInsteadOfToastr = true;

interface EditRecord {
  time: string;
  editor: string;
  change: {
    type: string;
  };
}

interface Props {
  params: {
    campaignId: string;
    entityId: string;
  };
  router: {
    push: (path: string) => void;
    goBack: () => void;
  };
}

const UCDSmartCommsCampaignDetailPage: React.FC<Props> = (props) => {
  const [actionType, setActionType] = useState<string | undefined>();
  const [isDuplicatePopupVisible, setIsDuplicatePopupVisible] = useState(false);
  const [showVersionHistoryModal, setShowVersionHistoryModal] = useState(false);
  const user = useSelector((state: any) => state.app.user);
  const request = useSelector((state: any) => state.requests);
  const projectView = useSelector((state: any) => state.ucd.projectView);
  const campaignView = useSelector((state: any) => state.ucd.campaignView);
  const featureFlagConfig = useSelector((state: any) => state.newTridents.featureFlagConfig);
  const isHolosPermissionEnabled = _.get(featureFlagConfig, 'holosPortalPermissionEnbaled', false); // TODO: remove this after holos portal permission is enabled for all users
  const countryID = _.get(projectView, 'project.countryID');
  const isTaiwanCreationAllowed = isTaiwanCampaignCreationAllowed(
    countryID,
    user.email,
  );
  const dispatch = useDispatch();
  const { campaignId, projectId } = useMemo(() => {
    return {
      campaignId: props.params.campaignId,
      projectId: props.params.entityId,
    };
  }, [props.params]);

  const { campaignRequest, projectRequest } = useMemo(
    () => ({
      campaignRequest: _.get(request, getRequestStateKey(GET_UCD_CAMPAIGN), {}),
      updateCampaignStatus: _.get(
        request,
        getRequestStateKey(UPDATE_UCD_CAMPAIGN_STATUS),
        {},
      ),
      projectRequest: _.get(request, getRequestStateKey(GET_UCD_PROJECT), {}),
    }),
    [request],
  );

  useEffect(() => {
    return () => {
      dispatch(resetUCDCampaign());
    };
  }, []);

  useEffect(() => {
    dispatch(
      getUCDCampaign(projectId, campaignId),
    );
    dispatch(getUCDProject(projectId));
  }, [projectId, campaignId]);

  useEffect(() => {
    if (campaignRequest.isFailure) {
      props.router.push(`${UCD_ROUTES_LINK.HOME()}?tab=campaigns`);
    }
  }, [campaignRequest]);

  useEffect(() => {
    dispatch(getFeatureFlagConfig());
  }, []);

  const isLoadingPage = useMemo(() => {
    const isCampaignDetailsAlreadyExist = campaignView
      ? campaignView.campaignID === props.params.campaignId
      : undefined;
    const isProjectDetailsExist = projectView
      ? projectView.projectID === props.params.entityId
      : undefined;

    return (
      isRequestsPending(campaignRequest) ||
      !isCampaignDetailsAlreadyExist ||
      isRequestsPending(projectRequest) ||
      !isProjectDetailsExist
    );
  }, [campaignView, campaignRequest, projectView, projectRequest]);


  const hasEditPermission = useMemo(() => {
    return (
      getValueByFeatureFlag(
        isHolosPermissionEnabled,
        hasPermission(user.userPermissions, UcdCampaignResourceName, UcdUniversalScope, 'update'),
        hasTridentPermission(user.userPermissions, '', '', 'update')
      ) &&
      campaignView &&
      campaignView.status === SmartCampaignStatus.draft
    );
  }, [user, campaignView]);

  const isActiveCampaign = useMemo(() => {
    return campaignView && campaignView.status === SmartCampaignStatus.active;
  }, [campaignView]);

  const isActiveOrUpcomingCampaign = useMemo(() => {
    return campaignView && (campaignView.status === SmartCampaignStatus.active || campaignView.status === SmartCampaignStatus.pending);
  }, [campaignView]);

  const canWithdrawCampaign = useMemo(() => {
    return (
      getValueByFeatureFlag(
        isHolosPermissionEnabled,
        hasPermission(user.userPermissions, UcdCampaignResourceName, UcdUniversalScope, 'update'),
        hasTridentPermission(user.userPermissions, '', '', 'update')
      ) && // Only user with update permission can withdraw
      isActiveCampaign &&
      campaignView &&
      dayjs().isBefore(dayjs(campaignView.campaign.start), 'day') && // Only before campaign start date
      user &&
      user.email === campaignView.createdBy // Only creator can withdraw
    );
  }, [isActiveCampaign, campaignView, user.userPermissions]);

  const canDeactivateCampaign = useMemo(() => {
    return (
      getValueByFeatureFlag(
        isHolosPermissionEnabled,
        hasPermission(user.userPermissions, UcdCampaignResourceName, UcdUniversalScope, 'update'),
        hasTridentPermission(user.userPermissions, '', '', 'update') || hasTridentPermission(user.userPermissions, '', '', 'active') // both creator and approver can deactivate
      ) && // Only user with update permission can deactivate
      isActiveOrUpcomingCampaign &&
      campaignView &&
      dayjs().isSameOrBefore(dayjs(campaignView.campaign.end), 'day') // Only before campaign end date
    );
  }, [isActiveCampaign, campaignView, user.userPermissions]);

  const tabs = useMemo(
    () => [
      {
        key: 'overview',
        label: 'Overview',
        children: (
          <SmartCampaignOverview
            actionType={actionType}
            setActionType={setActionType}
            router={props.router}
          />
        ),
      },
      {
        key: 'performance',
        label: 'Performance',
        children: <PerformancePage />,
      },
    ],
    [actionType],
  );

  const titleBarMenuProps = useMemo(
    () => ({
      items: [
        {
          icon: <EditOutlined />,
          key: 'edit',
          label: 'Edit',
          isItemAvailable: hasEditPermission,
          onClick: () => {
            props.router.push(
              UCD_ROUTES_LINK.SMART_CAMPAIGN_EDIT(projectId, campaignId),
            );
          },
        },
        {
          icon: <CopyOutlined />,
          isItemAvailable: isTaiwanCreationAllowed,
          key: 'duplicate',
          label: 'Duplicate',
          onClick: () => {
            setIsDuplicatePopupVisible(true);
          },
        },
        {
          icon: <UndoUniversal style={{ color: colorStatusAlertDefault }} />,
          isItemAvailable: canWithdrawCampaign,
          key: 'withdraw',
          label: (
            <span
              style={{
                color: colorStatusAlertDefault,
              }}
            >
              Withdraw
            </span>
          ),
          onClick: () => {
            dispatch(
              updateUCDCampaignStatus(
                SmartCampaignStatus.draft,
                user.email,
                projectId,
                campaignId,
              ),
            );
            scribe.sendEvent(
              ScribeEventName.CAMPAIGN_WITHDRAWN,
              processCampaignData({ campaignView, user }),
            );
            setActionType(ACTION_TYPE.WITHDRAW_UPCOMING);
          },
        },
        {
          icon: (
            <StopUniversal
              style={{
                color: colorStatusAlertDefault,
              }}
            />
          ),
          isItemAvailable: canDeactivateCampaign,
          key: 'deactivate',
          label: (
            <span
              style={{
                color: colorStatusAlertDefault,
              }}
            >
              Deactivate
            </span>
          ),
          onClick: () => {
            dispatch(
              updateUCDCampaignStatus(
                SmartCampaignStatus.deactivated,
                user.email,
                projectId,
                campaignId,
              ),
            );
            scribe.sendEvent(
              ScribeEventName.CAMPAIGN_DEACTIVATED,
              processCampaignData({ campaignView, user }),
            );
            setActionType(ACTION_TYPE.DEACTIVATE);
          },
        },
      ]
        .filter((item) => item.isItemAvailable)
        .map((item) => {
          delete item.isItemAvailable;
          return item;
        }),
    }),
    [hasEditPermission, canDeactivateCampaign, isTaiwanCreationAllowed],
  );

  const handleBackButtonClick = () => {
    props.router.goBack();
  };

  const handleShowVersionHistory = () => {
    setShowVersionHistoryModal(true);
  };

  const closeVersionHistoryModal = () => {
    setShowVersionHistoryModal(false);
  };

  const editRecords = useMemo(() => {
    return _.get(campaignView, 'edits', []).map((record: EditRecord) => ({
      time: record.time,
      editor: record.editor,
      changeType: record.change.type,
    }));
  }, [campaignView]);

  return (
    <div className="ucd-basic">
      {isLoadingPage ? (
        <Flex
          align="center"
          justify="center"
          style={{
            height: '100%',
          }}
        >
          <Spin />
        </Flex>
      ) : (
        <React.Fragment>
          <Flex align="center" justify="space-between">
            <Flex align="center" justify="center" gap={8}>
              <Button type="text" onClick={handleBackButtonClick}>
                <ArrowLeftUniversal style={{ fontSize: 20 }} />
              </Button>
              <Typography.Title
                style={{
                  display: 'contents',
                  color: colorBackgroundAltInverse,
                }}
                level={4}
              >
                {campaignView.campaign.name}
              </Typography.Title>
              <Dropdown menu={titleBarMenuProps}>
                <Button
                  icon={<MoreVerticalUniversal />}
                  shape="circle"
                  size="small"
                  type="text"
                />
              </Dropdown>
            </Flex>
          </Flex>

          {!_.isEmpty(editRecords) && (
            <div style={{ marginTop: 16, marginBottom: 8 }}>
              <a
                onClick={handleShowVersionHistory}
                style={{
                  color: '#1890ff',
                  cursor: 'pointer',
                  textDecoration: 'underline',
                  fontSize: 14
                }}
              >
                View version history
              </a>
            </div>
          )}

          <Divider style={{ margin: '16px 0px 0px 0px' }} />
          <Tabs
            animated
            items={tabs}
            size="middle"
            style={{ marginBottom: '0px' }}
            tabBarStyle={{ marginBottom: '0px' }}
            type="line"
          />

          <CampaignDuplicateConfirmationPopup
            visible={isDuplicatePopupVisible}
            onClose={() => setIsDuplicatePopupVisible(false)}
            projectId={projectId}
            campaignId={campaignId}
            templateType={TRIDENT_CAMPAIGN_TEMPLATE.smartSchedule}
            router={props.router}
          />

          {showVersionHistoryModal && (
            <Modal
              title="Version History"
              open={true}
              onCancel={closeVersionHistoryModal}
              footer={[
                <Button key="close" onClick={closeVersionHistoryModal}>
                  Close
                </Button>
              ]}
              width={800}
            >
              <VersionHistoryList
                records={editRecords}
                campaignID={campaignId}
                auditLogHost={_.get(featureFlagConfig, 'auditLogUrl', '')}
                auditLogIndex={_.get(featureFlagConfig, 'auditLogIndex', '')}
                changeTypeToResourceType={(_: string):string => {
                  return 'Campaign';
                }}
              />
            </Modal>
          )}
        </React.Fragment>
      )}
    </div>
  );
};

export default UCDSmartCommsCampaignDetailPage;
